import {ActionReducerMapBuilder} from "@reduxjs/toolkit";
import {RejectError, RejectErrorType, UnknownErrorCodes} from "./error/Error";

export enum SliceStatus {
  idle = "idle",
  loading = "loading",
  failed = "failed"
}

interface PendingCalls {
  readonly prefix: string;
  readonly requestId: string;
}

export interface BaseState {
  readonly pendings: Array<PendingCalls>;
  readonly status: SliceStatus;
  readonly error: RejectError | null;
}

export const createDefaultMatchers = <S extends BaseState>(p: Array<string> | string, builder: ActionReducerMapBuilder<S>) => {
  let prefixes: Array<string> = Array.isArray(p) ? p : [p];

  prefixes.forEach((prefix) => {
    createDefaultMatcher(prefix, builder);
  });
};

const createDefaultMatcher = <S extends BaseState>(prefix: string, builder: ActionReducerMapBuilder<S>) => {
  builder.addMatcher(
    (action) => {
      const actionPrefix = action.type.split("/pending")[0];
      return action.type.endsWith("/pending") && actionPrefix === prefix;
    },
    (draft, action) => {
      const prefix = action.type.split("/pending")[0];
      draft.status = SliceStatus.loading;
      draft.error = null; // Should store errors separatley?
      draft.pendings.push({
        prefix,
        requestId: action.meta.requestId
      });
    }
  );

  builder.addMatcher(
    (action) => {
      const actionPrefix = action.type.split("/fulfilled")[0];
      return action.type.endsWith("/fulfilled") && actionPrefix === prefix;
    },
    (draft, action) => {
      draft.status = SliceStatus.idle;
      const finishedRequestIndex = draft.pendings.findIndex((pending) => pending.requestId === action.meta.requestId);
      if (finishedRequestIndex > -1) {
        draft.pendings.splice(finishedRequestIndex, 1);
      }
    }
  );

  builder.addMatcher(
    (action) => {
      const actionPrefix = action.type.split("/rejected")[0];
      return action.type.endsWith("/rejected") && actionPrefix === prefix;
    },
    (draft, action) => {
      draft.status = SliceStatus.idle;
      const finishedRequestIndex = draft.pendings.findIndex((pending) => pending.requestId === action.meta.requestId);
      if (finishedRequestIndex > -1) {
        draft.pendings.splice(finishedRequestIndex, 1);
      }

      if (action.payload) {
        draft.error = action.payload;
      } else {
        draft.error = {
          message: action.error.message || "",
          code: UnknownErrorCodes.UNKNOWN,
          type: RejectErrorType.UNKNOWN
        };
      }
    }
  );
};
